import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CartProvider } from "./context/CartContext";
import { GiftCardCartProvider } from "./context/GiftCardCartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { SettingsProvider } from "./context/SettingsContext";
import { Navbar } from "./components/Navbar";
import { AnnouncementBar } from "./components/AnnouncementBar";
import { Footer } from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import SEO from "./components/SEO";

/*
 * Storefront entry points stay eager — these are the first paint for most visitors
 * and lazy-loading them would only add a spinner.
 */
import HomePage from "./pages/HomePage";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import LoginPage from "./pages/LoginPage";

/*
 * Everything below is split out. The admin bundle alone is the majority of the app's
 * JavaScript, and a shopper should never have to download it.
 */
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const OverviewPage = lazy(() => import("./pages/dashboard/OverviewPage"));
const SecurityPage = lazy(() => import("./pages/dashboard/SecurityPage"));
const SupportPage = lazy(() => import("./pages/dashboard/SupportPage"));
const GiftCardsPage = lazy(() => import("./pages/dashboard/GiftCardsPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const AddressesPage = lazy(() => import("./pages/AddressesPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

// Admin Pages — all lazy
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetails = lazy(() => import("./pages/admin/AdminOrderDetails"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminGiftCards = lazy(() => import("./pages/admin/AdminGiftCards"));
const AdminManualGiftCards = lazy(() => import("./pages/admin/AdminManualGiftCards"));
const AdminWallets = lazy(() => import("./pages/admin/AdminWallets"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminProfile = lazy(() => import("./pages/admin/AdminProfile"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminEnquiries = lazy(() => import("./pages/admin/AdminEnquiries"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminReturns = lazy(() => import("./pages/admin/AdminReturns"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));

/** Shown while a split chunk downloads. */
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Account, cart and checkout routes carry no public SEO value and must stay out of
 * the index. They also don't render <SEO> themselves, so without this they would
 * inherit whatever tags happened to be in index.html — which is exactly the
 * duplicate-canonical trap we just removed. Handled in one place so no page can
 * emit two competing sets of head tags.
 */
const PRIVATE_ROUTES = [
  { prefix: '/cart', title: 'Your Cart' },
  { prefix: '/checkout', title: 'Checkout' },
  { prefix: '/login', title: 'Sign In' },
  { prefix: '/register', title: 'Create Account' },
  { prefix: '/dashboard', title: 'My Account' },
  { prefix: '/order-success', title: 'Order Confirmed' },
  { prefix: '/order/', title: 'Order Details' },
  { prefix: '/wishlist', title: 'My Wishlist' },
  { prefix: '/profile', title: 'My Account' },
];

// Layout wrapper for storefront pages
function StorefrontLayout({ children }) {
  const location = useLocation();
  const hideNavFooter = ['/login', '/register'].includes(location.pathname);
  // The account area is an application, not a marketing page: a 500px footer of
  // shop links under "Change password" is noise, and it pushes the real content
  // off screen. The global header stays so the customer can leave for the store
  // at any point.
  const hideFooter = hideNavFooter || location.pathname.startsWith('/dashboard');
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <>
        <SEO title="Admin Panel" noIndex />
        {children}
      </>
    );
  }

  const privateRoute = PRIVATE_ROUTES.find((r) => location.pathname.startsWith(r.prefix));

  return (
    <>
      {privateRoute && <SEO title={privateRoute.title} noIndex />}
      {!hideNavFooter && <AnnouncementBar />}
      {!hideNavFooter && <Navbar />}
      {children}
      {!hideFooter && <Footer />}
    </>
  );
}

// App Router
function AppRouter() {
  return (
    <StorefrontLayout>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/product/:productId" element={<ProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* Two shapes for the same screen: the reset token may arrive as a query
            param or as a path segment, depending on how the email template builds
            the link. Only one of them is live, and a wrong guess here locks the
            customer out of the reset entirely. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/order-success/:orderId" element={<OrderSuccessPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/shipping" element={<ShippingPage />} />
        <Route path="/returns" element={<ReturnsPage />} />
        <Route path="/track-order" element={<TrackOrderPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />

        {/* User Dashboard Routes */}
        <Route path="/dashboard" element={<UserDashboard />}>
          <Route index element={<OverviewPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="gift-cards" element={<GiftCardsPage />} />
          <Route path="wallet" element={<WalletPage />} />
          <Route path="wishlist" element={<WishlistPage />} />
          <Route path="addresses" element={<AddressesPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="support" element={<SupportPage />} />
          {/* The old password-only page moved into the Security Centre. */}
          <Route path="settings" element={<Navigate to="/dashboard/security" replace />} />
          {/* Sections removed for having no backend — redirect rather than 404
              anyone holding an old link or bookmark. */}
          <Route path="rewards" element={<Navigate to="/dashboard" replace />} />
          <Route path="coupons" element={<Navigate to="/cart" replace />} />
          <Route path="notifications" element={<Navigate to="/dashboard/orders" replace />} />
          <Route path="preferences" element={<Navigate to="/dashboard/security" replace />} />
        </Route>

        {/* Order Details */}
        <Route path="/order/:orderId" element={<OrderSuccessPage />} />

        {/* Search & All Products */}
        <Route path="/search" element={<CategoryPage />} />
        <Route path="/products" element={<CategoryPage />} />

        {/* Admin login — shared login page; admins are routed to /admin on success */}
        <Route path="/admin/login" element={<LoginPage />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:orderId" element={<AdminOrderDetails />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:userId" element={<AdminUserDetail />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="giftcards" element={<AdminGiftCards />} />
          <Route path="manual-giftcards" element={<AdminManualGiftCards />} />
          <Route path="wallets" element={<AdminWallets />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="banners" element={<AdminBanners />} />
          <Route path="activity" element={<AdminActivityLog />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>

        <Route path="/profile" element={<Navigate to="/dashboard/orders" replace />} />

        {/* Catch all — a real 404, not the homepage. Rendering the homepage here made
            every mistyped URL a 200-status duplicate in the search index. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </StorefrontLayout>
  );
}

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <ScrollToTop />
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
          <SettingsProvider>
            <AuthProvider>
              <CartProvider>
                <GiftCardCartProvider>
                  <WishlistProvider>
                    <AppRouter />
                    <Toaster position="bottom-right" richColors closeButton duration={3000} />
                  </WishlistProvider>
                </GiftCardCartProvider>
              </CartProvider>
            </AuthProvider>
          </SettingsProvider>
        </GoogleOAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
